import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

export const CurriculumAccordion = ({
  courses,
  sectionLabel = 'Program Structure',
  title = 'Curriculum',
  description,
}) => {
  const [expanded, setExpanded] = useState(null);
  const toggle = (i) => setExpanded(expanded === i ? null : i);

  return (
    <section className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
            {sectionLabel}
          </p>
          <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
            {title}
          </h2>
          {description && (
            <p className="text-lg text-slate-600 max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {courses.map((course, index) => {
            const highlighted = course.isCapstone || course.isDissertation;
            const topicsLabel = course.isDissertation ? 'components' : 'topics';
            return (
              <motion.div
                key={course.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`border ${highlighted ? 'border-[#C82032]/30 bg-[#C82032]/5' : 'border-slate-200 bg-white'}`}
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#F1F5F8]/50 transition-colors"
                  data-testid={`course-toggle-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`font-mono text-sm font-bold ${highlighted ? 'text-[#C82032]' : 'text-[#163A59]'}`}>
                      {course.number}
                    </span>
                    <div>
                      <h3 className="font-semibold text-slate-900">{course.title}</h3>
                      <p className="text-sm text-slate-500">{course.duration}</p>
                    </div>
                  </div>
                  {expanded === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>

                <AnimatePresence>
                  {expanded === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2 border-t border-slate-200">
                        <p className="text-slate-600 mb-4">{course.description}</p>
                        <p className="text-sm font-medium text-slate-900 mb-3">Key {topicsLabel} include:</p>
                        <ul className="space-y-2">
                          {course.topics.map((topic) => (
                            <li key={topic} className="flex items-start gap-2 text-sm text-slate-600">
                              <span className="w-1.5 h-1.5 bg-[#163A59] rounded-full mt-2 flex-shrink-0"></span>
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
